/**
 * PaymentRequestsWidget — Brief 50.1 fee-earner homepage mini-panel.
 *
 * Compact "My Payment Requests" panel that shows the signed-in fee
 * earner their own OUTSTANDING payment requests (Pending / Approved /
 * Sent). Mounts above ``TodayWithMate`` in the fee-earner column of
 * ``Dashboard.jsx``.
 *
 * Design mirrors ``CreditControlWidget`` — same card shell, same row
 * shape (matter ref + client + amount + status short-code), same
 * ``/api``-based fetch. Distinct from the widget it copies in that:
 *   • it uses the Brief 50.1 ``mine=true`` + ``outstanding_only=true``
 *     query params on ``GET /hub/finance/payment-requests`` — the
 *     backend resolves the caller's initials from the session, so no
 *     initials leak from client to server;
 *   • it renders explicit loading / error / empty states (spec-locked
 *     copy) rather than hiding when no rows exist;
 *   • it auto-refreshes every 60 seconds;
 *   • its "See all →" link opens ``/finance/payment-tracker``.
 *
 * BM-H3488 (3 Oct 2026): the card is shown to Accounts only, so it now
 * asks for the firm's outstanding requests rather than ``mine=true``.
 * Accounts staff are not fee earners, so ``mine=true`` always came back
 * empty and May never saw a request on it.
 *
 * Internal role guard: the widget returns ``null`` unless the caller has
 * a resolvable fee-earner record AND holds the ACCOUNTS role (26 Sep
 * 2026 — Vanessa didn't want this on her own dashboard; it's May's
 * domain even though other fee earners/directors raise their own
 * requests too). The backend also short-circuits non-fee-earners to an
 * empty result set, so this is defence in depth.
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  PoundSterling,
  RefreshCw,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { API } from "../config/api";
import { getUserRole, ROLES } from "../config/roleConfig";

// Brief 50.1: short-code palette for outstanding statuses. Mirrors
// ``STAGE_SHORT`` in ``CreditControlWidget`` so the two widgets read
// as a coherent pair when both render on the same home page.
const STATUS_SHORT = {
  Pending:  { label: "PEND", color: "text-status-amber" },
  Approved: { label: "APPR", color: "text-yellow-400" },
  Sent:     { label: "SENT", color: "text-teal" },
};

function resolveEmail() {
  return (
    sessionStorage.getItem("bonsai_user_email") ||
    localStorage.getItem("bonsai_user_email") ||
    (() => {
      try {
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        return user.user_email || user.email || user.ms_email || "";
      } catch { return ""; }
    })()
  ).toLowerCase().trim();
}

function headers() {
  return {
    "X-Bonsai-User-Id":
      localStorage.getItem("bonsai_user_id") ||
      sessionStorage.getItem("bonsai_user_id") ||
      "",
    "X-User-Email": resolveEmail(),
  };
}

export function formatAmount(raw) {
  // Guard against the pre-Number("") === 0 and Number(null) === 0
  // coercions — an empty amount cell must read as "—", not £0.00.
  if (raw === null || raw === undefined || raw === "") return "—";
  const n = Number(raw);
  if (!Number.isFinite(n)) return "—";
  return `£${n.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// Exported for the Jest unit test — mirrors the OUTSTANDING_STATUSES
// tuple pinned server-side in routes_finance_payment_tracker.py. Any
// drift here silently changes the widget's status short-codes.
export const OUTSTANDING_STATUSES = ["Pending", "Approved", "Sent"];

export function statusShortCode(status) {
  const badge = STATUS_SHORT[status];
  if (badge) return badge.label;
  return (status || "?").slice(0, 4).toUpperCase();
}

// BM-H3488: the tracker's real route. "See all" used to link to
// /payment-tracker, which fell through to the catch-all and bounced the
// user to Bonsai Mail.
export const PAYMENT_TRACKER_PATH = "/finance/payment-tracker";

export function feeEarnerTrackerHref(initials) {
  if (!initials) return PAYMENT_TRACKER_PATH;
  return `${PAYMENT_TRACKER_PATH}?fee_earner=${encodeURIComponent(initials)}`;
}

export default function PaymentRequestsWidget() {
  const email = resolveEmail();
  // Accounts-only (26 Sep 2026): Vanessa doesn't want this on her own
  // dashboard — payment requests are May's (ACCOUNTS) domain, not every
  // fee earner's or director's, even though they raise their own requests.
  // BM-H3488: Accounts staff are not fee earners, so the card no longer
  // requires a fee-earner record; it shows the firm's outstanding queue.
  if (getUserRole(email) !== ROLES.ACCOUNTS) return null;
  return <PaymentRequestsWidgetInner />;
}

function PaymentRequestsWidgetInner() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setError("");
    try {
      const res = await axios.get(`${API}/hub/finance/payment-requests`, {
        // BM-H3488: no mine=true. That filtered to the caller's own
        // fee-earner requests, and Accounts staff have none, so the card
        // was empty for its only audience. Accounts can read the full
        // tracker, so ask for every outstanding request.
        params: { outstanding_only: true, limit: 50 },
        headers: headers(),
      });
      setRows(Array.isArray(res.data?.rows) ? res.data.rows : []);
      setTotal(Number(res.data?.total || 0));
    } catch (err) {
      setError("Couldn't load your payment requests");
      // Deliberately not clearing rows on error — a stale row set is
      // more useful than a wipe, and the error band signals staleness.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    // 60s auto-refresh — matches the spec footer polling contract.
    const iv = setInterval(load, 60_000);
    return () => clearInterval(iv);
  }, [load]);

  const visibleRows = rows.slice(0, 5);
  const goToTracker = () => navigate(PAYMENT_TRACKER_PATH);

  return (
    <div
      className="bg-navy-card border border-white/10 rounded-xl p-4 mb-4"
      data-testid="payment-requests-widget"
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold text-white flex items-center gap-2">
          <PoundSterling className="w-4 h-4 text-teal" />
          Payment Requests
        </h3>
        <button
          onClick={goToTracker}
          className="text-xs text-teal hover:underline"
          data-testid="payment-requests-widget-see-all"
        >
          See all →
        </button>
      </div>

      {loading ? (
        <div
          className="space-y-1.5"
          data-testid="payment-requests-widget-loading"
        >
          {[0, 1, 2].map(i => (
            <div
              key={i}
              className="flex items-center justify-between bg-white/5 rounded-lg px-3 py-2 animate-pulse"
            >
              <div className="h-3 bg-white/10 rounded w-2/3" />
              <div className="h-3 bg-white/10 rounded w-16" />
            </div>
          ))}
        </div>
      ) : error ? (
        <div
          className="flex items-center gap-2 bg-status-red-dim/20 border border-status-red/30 rounded-lg px-3 py-2"
          data-testid="payment-requests-widget-error"
        >
          <AlertCircle className="w-4 h-4 text-status-red shrink-0" />
          <span className="text-xs text-status-red flex-1">{error}</span>
          <button
            onClick={() => { setLoading(true); load(); }}
            className="text-status-red hover:text-white shrink-0"
            aria-label="Retry"
            data-testid="payment-requests-widget-retry"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : visibleRows.length === 0 ? (
        <p
          className="text-xs text-gray-500 py-3 text-center"
          data-testid="payment-requests-widget-empty"
        >
          No outstanding payment requests
        </p>
      ) : (
        <div className="space-y-1.5">
          {visibleRows.map(r => {
            const badge = STATUS_SHORT[r.payment_status]
              || { label: (r.payment_status || "?").slice(0, 4).toUpperCase(), color: "text-gray-400" };
            return (
              <div
                key={r.id}
                onClick={() => navigate(`/submission/${r.id}`)}
                className="flex items-center justify-between bg-white/5 rounded-lg px-3 py-2 cursor-pointer hover:bg-white/10 transition-colors gap-3"
                data-testid={`payment-requests-widget-row-${r.id}`}
              >
                <div className="flex-1 min-w-0 flex items-baseline gap-2">
                  <span className="text-[11px] font-mono text-gray-400 shrink-0">
                    {r.payment_number || "—"}
                  </span>
                  <span className="text-[11px] font-mono text-gray-500 shrink-0 max-w-[130px] truncate">
                    {r.matter_reference || "—"}
                  </span>
                  <span className="text-sm text-white truncate">
                    {r.client_name || "—"}
                  </span>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-sm font-bold text-white tabular-nums text-right">
                    {formatAmount(r.amount)}
                  </span>
                  <span
                    className={`text-[10px] font-bold ${badge.color} w-10 text-right`}
                    data-testid={`payment-requests-widget-status-${r.id}`}
                  >
                    {badge.label}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="border-t border-white/10 mt-3 pt-2 flex justify-between items-center text-xs">
        <span
          className="text-gray-400"
          data-testid="payment-requests-widget-footer-count"
        >
          {loading ? "…" : `${total} total outstanding`}
        </span>
        <button
          onClick={() => { setLoading(true); load(); }}
          className="text-gray-400 hover:text-white"
          aria-label="Refresh"
          data-testid="payment-requests-widget-refresh"
        >
          {loading
            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
            : <RefreshCw className="w-3.5 h-3.5" />
          }
        </button>
      </div>
    </div>
  );
}
